import { useState } from "react";
import {
  Button,
  TextInput,
  PasswordInput,
  PinInput,
  Divider,
  Center,
  Group,
  Text,
  UnstyledButton,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { EMAIL_REGEX } from "../../../../utils/regex";
import { successNotifications } from "../../../../utils/handleNotifications";
import classes from "./MultiLoginForm.module.css";

const VIEW = {
  SIGN_IN: "signin",
  OTP: "otp",
};

function MultiLoginForm({
  onLoginPassword,
  isSubmitting,
  onSendOtp,
  onLoginOtp,
}) {
  const [view, setView] = useState(VIEW.SIGN_IN);

  const form = useForm({
    initialValues: {
      email: "",
      password: "",
    },
    validate: {
      email: (value) =>
        EMAIL_REGEX.test(value) ? null : "Enter valid email",
    },
  });

  const formOtp = useForm({
    initialValues: {
      otp: "",
    },
    validate: {
      otp: (value) =>
        value.trim().length < 6 ? "Enter the 6-digit code" : null,
    },
  });

  const handleSignIn = (values) => {
    if (!values.password.trim()) {
      form.setFieldError("password", "Enter your password");
      return;
    }

    onLoginPassword(values);
  };

  const handleSendOtp = () => {
    const emailValidation = form.validateField("email");
    if (emailValidation.hasError) {
      return;
    }

    onSendOtp({ email_or_phone: form.values.email }).then(() => {
      formOtp.setFieldValue("otp", "");
      setView(VIEW.OTP);
    });
  };

  const handleSignInKeyPress = (event) => {
    if (event.key === "Enter") {
      form.onSubmit(handleSignIn)();
    }
  };

  if (view === VIEW.SIGN_IN) {
    return (
      <>
        <TextInput
          placeholder="Enter your email address"
          label="Email address"
          type="email"
          size="md"
          variant="filled"
          {...form.getInputProps("email")}
        />

        <PasswordInput
          mt="md"
          label="Password"
          placeholder="Enter your password"
          size="md"
          variant="filled"
          {...form.getInputProps("password")}
          onKeyDown={handleSignInKeyPress}
        />

        <Button
          fullWidth
          onClick={form.onSubmit(handleSignIn)}
          variant="gradient"
          mt="xl"
          loading={isSubmitting}
          loaderProps={{ type: "dots" }}
        >
          Sign in
        </Button>

        <Divider label="Or" labelPosition="center" my="xl" />

        <Button variant="outline" fullWidth onClick={handleSendOtp}>
          Send one-time code instead
        </Button>
      </>
    );
  }

  return (
    <>
      <Text className={classes.fieldLabel}>Email</Text>
      <UnstyledButton
        className={classes.emailChip}
        onClick={() => setView(VIEW.SIGN_IN)}
      >
        <Text span className={classes.emailChipText}>
          {form.values.email}
        </Text>
        <Text span className={classes.emailChipAction}>
          Change
        </Text>
      </UnstyledButton>

      <Text className={classes.otpHint} mt="md">
        Enter the 6-digit code sent to your email
      </Text>

      <Center mt="xl">
        <PinInput
          label="Verification code"
          type="number"
          length={6}
          size="md"
          {...formOtp.getInputProps("otp")}
        />
      </Center>

      <Group mt="xl" justify="space-between" className={classes.controls}>
        <Button
          variant="subtle"
          onClick={() => {
            onSendOtp({ email_or_phone: form.values.email }).then(() => {
              successNotifications("OTP has been resent");
            });
          }}
          className={classes.control}
        >
          Resend code
        </Button>

        <Button
          variant="gradient"
          className={classes.control}
          onClick={formOtp.onSubmit(onLoginOtp)}
          loading={isSubmitting}
          loaderProps={{ type: "dots" }}
        >
          Verify and sign in
        </Button>
      </Group>
    </>
  );
}

export default MultiLoginForm;
